import React, { useMemo, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
    Box,
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    useTheme,
    Tooltip
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Header from '../../global/Header';
import { ArrowForward } from '@mui/icons-material';
import { useDiscreteMode } from '../../global/DiscreteMode';

import { useGetApisChargesFeedQuery } from '../../../../features/organizations/organizationsApiSlice';
import { tokens } from '../../../../theme';

const numberFormat = new Intl.NumberFormat();

const fmtEpoch = (epoch) => {
    if (!epoch) return '';
    const d = new Date(Number(epoch) * 1000);
    if (Number.isNaN(d.getTime())) return '';
    return d.toISOString().replace('T', ' ').slice(0, 19) + 'Z';
};

const ChargesFeed = () => {
    const navigate = useNavigate();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isDiscreteMode = useDiscreteMode();
    const [range, setRange] = useState('7d'); // day|7d|30d|all
    const [q, setQ] = useState('');

    const { data, isLoading, isError } = useGetApisChargesFeedQuery(
        { range, q: q.trim() || undefined },
        { pollingInterval: 15000, refetchOnFocus: true, refetchOnMountOrArgChange: true }
    );

    const rowsRaw = data?.rows || [];

    const rows = useMemo(() => {
        return rowsRaw.map((r) => ({
            id: String(r.request_id),
            request_id: String(r.request_id),
            org_id: String(r.org_id),
            org_name: r.org_name ? String(r.org_name) : '',
            api_key_id: r.api_key_id ? String(r.api_key_id) : '',
            cost: Number(r.cost || 0),
            created_at: r.created_at ?? null,
        }));
    }, [rowsRaw]);

    const aggregate = useMemo(() => {
        let totalCost = 0;
        const orgs = new Set();
        for (const r of rows) {
            totalCost += Number(r.cost || 0);
            if (r.org_id) orgs.add(String(r.org_id));
        }
        return { requests: rows.length, orgs: orgs.size, totalCost };
    }, [rows]);

    const handleExportCSV = () => {
        if (!rows.length) return;

        const headers = ['created_at', 'org_id', 'org_name', 'api_key_id', 'cost', 'request_id'];
        const csvRows = [headers.join(',')];

        rows.forEach((r) => {
            const values = headers.map((h) => {
                let v = r[h];
                if (h === 'created_at') v = fmtEpoch(v);
                if (typeof v === 'string') return `"${v.replace(/"/g, '""')}"`;
                return v ?? '';
            });
            csvRows.push(values.join(','));
        });

        const csvContent = csvRows.join('\n');
        const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `api_charges_${range}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const columns = [
        { field: 'created_at', headerName: 'Created (UTC)', flex: 0.9, valueFormatter: ({ value }) => fmtEpoch(value) },
        {
            field: 'org_name',
            headerName: 'Org',
            flex: 1.1,
            sortable: false,
            renderCell: (params) => {
                const orgId = params.row?.org_id;
                if (!orgId) return params.value;

                const apiHome = `/console/organization/${orgId}?tab=APIs`;

                return (
                    <Tooltip title="Open org APIs">
                        <Box
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(apiHome);
                            }}
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                cursor: 'pointer',
                                fontWeight: 600,
                                color: colors.greenAccent[300],
                                '&:hover': {
                                    textDecoration: 'underline',
                                    color: colors.greenAccent[200],
                                },
                            }}
                        >
                            <span>{params.value}</span>
                            <ArrowForward sx={{ fontSize: 12, opacity: 0.85 }} />
                        </Box>
                    </Tooltip>
                );
            },
        },
        {
            field: 'org_id',
            headerName: 'Org ID',
            flex: 0.8,
        },
        { field: 'api_key_id', headerName: 'API Key ID', flex: 0.75 },
        { field: 'cost', headerName: 'Cost', flex: 0.4, valueFormatter: ({ value }) => numberFormat.format(Number(value || 0)) },
        { field: 'request_id', headerName: 'Request ID', flex: 1.2 },
    ];

    if (isLoading) return <div>Loading...</div>;
    if (isError) return <div>Error occurred while fetching charges</div>;

    return (
        <Box mt={2}>
            <Header title="Charges" subtitle="Per-request charges recorded to DDB" />

            <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={3}>
                    <FormControl sx={{ m: 1, minWidth: 160 }}>
                        <InputLabel id="range-label">Range</InputLabel>
                        <Select
                            labelId="range-label"
                            value={range}
                            onChange={(e) => setRange(e.target.value)}
                            label="Range"
                        >
                            <MenuItem value="day">Last 24h</MenuItem>
                            <MenuItem value="7d">Last 7 days</MenuItem>
                            <MenuItem value="30d">Last 30 days</MenuItem>
                            <MenuItem value="all">All time</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12} md={5}>
                    <TextField
                        label="Search (org name/id, api key id, request id)"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        sx={{ m: 1, width: '100%', "& .MuiOutlinedInput-notchedOutline": { borderWidth: "2px" } }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', gap: 2, m: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                        <Button
                            variant="outlined"
                            onClick={handleExportCSV}
                            sx={{ borderColor: 'lightgreen', color: 'lightgreen', '&:hover': { borderColor: 'green', color: 'green' } }}
                        >
                            Export CSV
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
                <Typography variant="h4">Charges Summary ({range})</Typography>
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12} sm={4}>
                        <Typography>Requests: {numberFormat.format(aggregate.requests)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography>Orgs: {numberFormat.format(aggregate.orgs)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Typography>Total Cost: {numberFormat.format(aggregate.totalCost)}</Typography>
                    </Grid>
                </Grid>
            </Paper>

            <DataGrid
                rows={rows}
                columns={columns}
                sx={{
                    height: 650,
                }}
                disableColumnMenu
                pageSizeOptions={[25, 50, 100]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 50, page: 0 } },
                    sorting: { sortModel: [{ field: 'created_at', sort: 'desc' }] }
                }}
            />
        </Box>
    );
};

export default ChargesFeed;
